// upgradeMonitoringTable.tsx
//
// (c) NCR Voyix Corporation 2025. All rights reserved.

import React, { useEffect } from 'react'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import { GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid-pro'
import { connect } from 'react-redux'
import Typography from '@mui/material/Typography'
import { AppThunkDispatch, RootState } from '../../../redux'
import { SelectedSite, SiteMonitoring } from '../../../models/upgradeMonitoring/upgradeMonitoring'
import Table from '../../../components/Table'
import { ProgressBar } from '../../../components/ProgressBar'
import { GreenSuccess, RedError } from '../../../common/constants/colorConstants'
import { siteUpgradeMonitoring } from '../../../redux/upgradeMonitoring/thunks'
import { setSelectedSite } from '../../../redux/upgradeMonitoring/slice'

function mapDispatchToProps(dispatch: AppThunkDispatch) {
  return {
    dispatchGetSites: (...args: Parameters<typeof siteUpgradeMonitoring>) => dispatch(siteUpgradeMonitoring(...args)),
    dispatchSetSelectedSite: (...args: Parameters<typeof setSelectedSite>) => dispatch(setSelectedSite(...args))
  }
}

function mapStateToProps(state: RootState) {
  return {
    user: state.user,
    company: state.company,
    sites: state.upgradeMonitoring.sites,
    requestState: state.upgradeMonitoring.siteUpgradeMonitoringState,
    refreshRate: state.upgradeMonitoring.refreshRate
  }
}

function getFormattedDate(date: string): string {
  return new Date(date).toLocaleString(undefined, {
    hour12: false, year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit'
  })
}

function ScheduledTime(props: Readonly<{ scheduleType: 'Download' | 'Upgrade', date: string }>): JSX.Element {
  return (
    <Typography>
      {`${props.scheduleType}: ${getFormattedDate(props.date)}`}
    </Typography>
  )
}

type DispatchProps = ReturnType<typeof mapDispatchToProps>
type StateProps = ReturnType<typeof mapStateToProps>

type UpgradeMonitoringTableProps = DispatchProps & StateProps

const checkIcon = <CheckCircleIcon sx={{ color: GreenSuccess }} />// color="success" />
const cancelIcon = <CancelIcon sx={{ color: RedError }} />// color="error" />

const columns: GridColDef[] = [
  {
    field: 'id',
    headerName: 'Site ID',
    flex: 1,
    maxWidth: 100
  },
  {
    field: 'siteName',
    headerName: 'Site Name',
    flex: 1,
    minWidth: 100
  },
  {
    field: 'timezone',
    headerName: 'Timezone',
    flex: 1,
    minWidth: 120
  },
  {
    field: 'schedule',
    headerName: 'Schedule',
    flex: 1,
    minWidth: 240,
    renderCell: (params: GridRenderCellParams) => (
      <div>
        {
          params.row.schedule?.download
          && params.row.schedule.download !== ''
          && <ScheduledTime scheduleType="Download" date={params.row.schedule.download} />
        }
        {
          params.row.schedule?.upgrade
          && params.row.schedule.upgrade !== ''
          && <ScheduledTime scheduleType="Upgrade" date={params.row.schedule.upgrade} />
        }
      </div>
    )
  },
  {
    field: 'package',
    headerName: 'Package',
    flex: 1,
    maxWidth: 85,
    minWidth: 85,
    renderCell: (params: GridRenderCellParams) => params.row.package != null && (params.row.package ? checkIcon : cancelIcon)
  },
  {
    field: 'progress',
    headerName: 'Progress',
    flex: 1,
    renderCell: (params: GridRenderCellParams) => params.row.progress != null && <ProgressBar value={params.row.progress} error={params.row.state === 'Failure'} />
  },
  {
    field: 'isBusinessReady',
    headerName: 'Ready for Business',
    flex: 1,
    minWidth: 150,
    renderCell: (params: GridRenderCellParams) => params.row.isBusinessReady && (<Typography sx={{ color: GreenSuccess }}><strong>Site Ready</strong></Typography>)
  },
  {
    field: 'state',
    headerName: 'State',
    flex: 1
  },
  {
    field: 'checkpoint',
    headerName: 'Checkpoint',
    flex: 1
  }
]

export function UpgradeMonitoringTable(
  {
    company,
    user,
    sites,
    requestState,
    dispatchGetSites,
    dispatchSetSelectedSite,
    refreshRate
  }: UpgradeMonitoringTableProps
): JSX.Element {
  useEffect(() => {
    if (
      user.dataCenter
      && user.userId
      && user.sessionId
    ) {
      const { userId, sessionId, dataCenter } = user
      dispatchGetSites({
        userId,
        sessionId,
        dataCenter,
        companyid: company.selectedCompany?.id
      })
    }
  }, [company.selectedCompany?.id])

  useEffect(() => {
    if (refreshRate.value > 0) {
      const intervalId = setInterval(() => {
        if (user.dataCenter && user.userId && user.sessionId) {
          const { userId, sessionId, dataCenter } = user
          dispatchGetSites({
            userId,
            sessionId,
            dataCenter,
            companyid: company.selectedCompany?.id
          })
        }
      }, refreshRate.value)
      return () => clearInterval(intervalId)
    }
    return undefined
  }, [refreshRate, company.selectedCompany?.id])

  const rows = sites.map((d: SiteMonitoring) => ({
    id: d.siteId,
    siteName: d.siteName,
    timezone: d.timezone,
    schedule: { download: d.scheduledDownload, upgrade: d.scheduledUpgrade },
    package: d.isPackageJsonValid,
    isBusinessReady: d.isBusinessReady,
    progress: d.progress,
    state: d.agentState,
    checkpoint: d.checkpointState
  }))

  const handleRowClick = (site: GridRowParams) => {
    const {
      id,
      siteName
    } = site.row
    const selectedSite: SelectedSite = {
      siteId: id,
      siteName
    }
    dispatchSetSelectedSite(selectedSite)
  }

  return (
    <Table
      width="100%"
      rows={requestState === 'pending' ? [] : rows}
      columns={columns}
      toolbar
      loading={requestState === 'pending'}
      disableSelectionOnClick
      onRowClick={(e) => handleRowClick(e)}
    />
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(UpgradeMonitoringTable)
